/* eslint-disable prettier/prettier, react/prop-types, react/react-in-jsx-scope */
import styled from '@emotion/styled';
import { useState } from 'react';

import Facebook from '../../Builder/Library/Images/Social/Facebook';
import Instagram from '../../Builder/Library/Images/Social/Instagram';
import Linkedin from '../../Builder/Library/Images/Social/Linkedin';
import Twitter from '../../Builder/Library/Images/Social/TwitterSquare';
import Youtube from '../../Builder/Library/Images/Social/Youtube';
import { pbFunctions } from '../../PageBuilder/globals';
import { isItemVisible } from '../../PageBuilder/helpers/content.js';
import Linking from '../../PageBuilder/helpers/linking';
import { cmsLegacyThemeConnector } from '../../PageBuilder/redux/connector';
import { connectLocation } from '../../location/LocationConnectors';
import wrappedInReduxProvider from '../../wrappedInReduxProvider';
import Text from '../oc-text';

const restructureHelpers = {
  setImageIconToList: (arr = [], { aria_label, link }) => {
    if (arr?.length === 0 || arr[arr?.length - 1]?.details?.iconList?.length === 5) {
      const listCopy = [...arr];
      listCopy.push({
        details: {
          is_icon: true,
          iconList: [
            {
              selected: aria_label?.toLowerCase(),
              url: link,
            },
          ],
        },
      });
      return listCopy;
    }
    if (arr?.length > 0 && !arr[arr?.length - 1].details?.iconList) {
      const listCopy = [...arr];
      listCopy[listCopy?.length - 1].details = {
        ...listCopy[listCopy?.length - 1]?.details,
        is_icon: true,
        iconList: [
          {
            selected: aria_label?.toLowerCase(),
            url: link,
          },
        ],
      };
      return listCopy;
    }
    if (arr?.length > 0 && arr[arr?.length - 1].details.iconList) {
      const listCopy = [...arr];
      listCopy[listCopy.length - 1].details.iconList.push({
        selected: aria_label?.toLowerCase(),
        url: link,
      });
      return listCopy;
    }
  },
  setTitleToList: (arr = [], { title }) => {
    const listCopy = [...arr];
    listCopy.push({
      details: {
        is_icon: true,
        subheading: title,
      },
    });
    return listCopy;
  },
  splitBottomLinks: (bottomLinks = []) => {
    const half = Math.ceil(bottomLinks.length / 2);

    return {
      left: bottomLinks?.slice(0, half),
      right: bottomLinks?.slice(half),
    };
  },
};

const restructureLegacyData = {
  headerDetailsFn: heading => ({
    has_header: !!heading,
    title: heading,
  }),
  footerItemsFn: (cleanGroups = []) =>
    cleanGroups.map(({ title, links, html_class = null, always_expanded }) => ({
      details: {
        title,
        always_expanded,
      },
      children:
        html_class != 'social-media-links'
          ? links.map(({ title, link }) => ({
              details: {
                title,
                url: link,
              },
            }))
          : links.reduce((prev, current) => {
              if (current.image) {
                return restructureHelpers.setImageIconToList(prev, current);
              }

              if (current.title) {
                return restructureHelpers.setTitleToList(prev, current);
              }
              return prev;
            }, []),
    })),
  copyrightFn: registeredStatement => ({
    paragraph: registeredStatement,
  }),
  quickLinksFn: (bottomLeftLinks, bottomRightLinks, bottomLinks) => {
    console.log(
      'split',
      restructureHelpers.splitBottomLinks(bottomLinks).left,
      restructureHelpers.splitBottomLinks(bottomLinks).right
    );
    bottomLeftLinks = bottomLinks ? restructureHelpers.splitBottomLinks(bottomLinks).left : bottomLeftLinks;
    bottomRightLinks = bottomLinks ? restructureHelpers.splitBottomLinks(bottomLinks).right : bottomRightLinks;
    return {
      left: bottomLeftLinks?.map(({ text, path, title, link }) => ({
        title: text || title,
        url: path || link,
      })),
      right: bottomRightLinks?.map(({ text, path, title, link }) => ({
        title: text || title,
        url: path || link,
      })),
    };
  },
  removeEmptyfooterItems: (groups = []) =>
    groups.map(x => {
      if (x?.html_class != 'social-media-links') {
        return {
          ...x,
          links: x?.links?.filter(({ title, link }) => !!(title.trim() && link.trim())),
        };
      }
      return x;
    }),
};

const FooterContainer = cmsLegacyThemeConnector(styled.footer`
  background-color: ${props => props.legacyTheme.colors.sharedGrey};
  margin: auto;
  max-width: max-content;
  position: relative;

  @media screen and (min-width: 1200px) {
    max-width: 1440px;
  }

  &.tenant-paroc {
    padding-top: 35px;

    &:before {
      background-image: repeating-linear-gradient(307.5deg, #ce1126 -4.7%, #ce1126 6.5%, #ffffff 0, #ffffff 19%);
      content: '';
      display: inline-block;
      height: 16px;
      left: 0;
      position: absolute;
      top: 0;
      width: 100%;

      @media screen and (min-width: 768px) {
        background-image: repeating-linear-gradient(307.5deg, #ce1126 -2.8%, #ce1126 4.4%, #ffffff 0, #ffffff 11.2%);
      }

      @media screen and (min-width: 1200px) {
        background-image: repeating-linear-gradient(307.5deg, #ce1126 0, #ce1126 3.8%, #ffffff 0, #ffffff 7.4%);
      }
    }
  }

  .tenant-fleetwoodusa & {
    background-color: black;
    color: white;

    h1, h2, h3, h4, h5, h6 {
      color: white;
    }
  }
`);

const FooterElements = styled.div`
  margin: auto;
  padding: 20px 15px;

  ul {
    list-style: none;
    padding-left: 0;
  }

  a {
    text-decoration: none;
  }

  .links {
    &:hover {
      text-decoration: underline;
    }

    &:focus {
      text-decoration: underline;
    }

    @media screen and (min-width: 768px) {
      margin-left: 5px;
    }
  }

  @media screen and (min-width: 768px) {
    max-width: 800px;
  }

  @media screen and (min-width: 1200px) {
    max-width: 1185px;
  }
`;

const ContentsContainer = styled.div`
  display: grid;
  margin-bottom: 15px;
  width: 100%;

  @media screen and (min-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    row-gap: 10px;
  }

  @media screen and (min-width: 1200px) {
    display: flex;
    flex-wrap: nowrap;
    gap: 10px;
  }
`;

const Column = styled.ul`
  border-bottom: 1px solid white;
  margin: 0;

  .column--list {
    padding: 0;
    margin: 0;
  }

  .collapsible__content {
    max-height: 0;
    opacity: 0;
    overflow: hidden;
    transition: all 0.2s;
  }

  .collapsible--expanded {
    .icon--chevron {
      transform: rotate(90deg);
    }
  }

  .collapsible--expanded + .collapsible__content {
    max-height: 100%;
    opacity: 1;
  }

  @media screen and (min-width: 768px) {
    border-bottom: 0;
    .collapsible__content {
      max-height: 100%;
      opacity: 1;
    }
  }

  @media screen and (min-width: 1200px) {
    flex: 1;
  }

  .collapsible__content {
    &.always_expanded {
      max-height: 100%;
      opacity: 1;

      .icon--chevron {
        visibility: hidden;
      }
    }
  }

  .column--list {
    .icon--chevron {
      &.hide_icon {
        visibility: hidden;
      }
    }
  }
`;

const ColumnTitle = styled.h3`
  color: #000000;
  font-family: "OCRoboto", sans-serif;
  font-size: 18px;
  font-weight: 700;
  line-height: 22px;
  margin-bottom: 5px;
  margin: 0;
  padding: 20px 0;
  text-transform: none;

  @media screen and (min-width: 768px) {
    margin-bottom: 10px;
    margin-left: 5px
    padding: 0px;
  }

  .tenant-fleetwoodusa & {
    color: white;
  }
`;

const LinkList = cmsLegacyThemeConnector(styled.li`
  &.column--list_item {
    margin-bottom: 9px;
    margin-left: 0px;
    margin: 5px;
  }

  a {
    color: black;
    cursor: pointer;
    display: block;
    font-family: 'OCRoboto', sans-serif;
    font-size: 18px;
    padding: 10px 0;
    text-decoration: none;
  }

  @media screen and (min-width: 768px) {
    a {
      font-size: 16px;
      padding: 0px;
    }
  }

  &.icon__container {
    .icon--group {
      align-items: center;
      display: flex;
      margin-bottom: 8px;
      margin-top: 3px;
      padding-left: 5px;
    }

    .icon--group {
      .icon--wrapper {
        background: linear-gradient(black, black);
        background-position: 50% 50%;
        background-repeat: no-repeat;
        background-size: 0% 0%;
        cursor: pointer;
        padding: 10px;
        position: relative;
        transition: background-size 0.1s;

        &:hover {
          background-size: 100% 100%;

          svg {
            fill: ${props => props.legacyTheme.colors.sharedGrey};
          }
        }
      }
    }
  }

  b {
    font-family: 'OCRoboto', sans-serif;
    font-size: 18px;
    text-transform: none;
  }

  @media screen and (min-width: 768px) {
    b {
      font-size: 16px;
    }
  }

  .tenant-fleetwoodusa & {
    a {
      color: white;

      &:hover {
        color: ${props => props.legacyTheme.colors.sharedGrey};
        text-decoration: none;
      }
    }

    &.icon__container {
      .icon--group {
        .icon--wrapper {
          svg {
            fill: white;
          }

          &:hover {
            svg {
              fill: ${props => props.legacyTheme.colors.sharedGrey};
            }
          }
        }
      }
    }
  }
`);

const TitleContainer = styled.span`
  cursor: pointer;
  display: flex;
  justify-content: space-between;

  &:focus {
    outline: 2px solid #d40f7d;
    outline-offset: 2px;
  }

  i {
    font-family: 'icomoon';
    font-size: 20px;
    font-weight: 700;
    margin-bottom: auto;
    margin-right: 10px;
    margin-top: auto;
    transform: rotate(-90deg);

    @media screen and (min-width: 768px) {
      display: none;
    }
  }

  @media screen and (min-width: 768px) {
    cursor: default;

    &:focus {
      outline: none;
    }
  }

  .tenant-fleetwoodusa & {
    color: white;

    &:focus {
      outline-color: white;
    }
  }
`;

const BottomLinksContainer = styled.div``;

const BottomLinksContents = cmsLegacyThemeConnector(styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);

  a {
    color: black;
    cursor: pointer;
    display: block;
    font-family: 'OCRoboto', sans-serif;
    font-size: 14px;
    padding: 10px 0;
    text-decoration: none;
  }

  ul {
    margin-bottom: 8px;
    margin-top: 10px;

    li {
      margin: 0px;
    }
  }

  @media screen and (min-width: 768px) {
    border-top: 2px solid black;
    display: flex;
    justify-content: space-between;

    ul {
      align-items: space-between;
      column-gap: 25px;
      display: flex;
    }
  }

  @media screen and (min-width: 1200px) {
    a {
      font-size: 16px;
    }
  }

  .tenant-fleetwoodusa & {
    border-color: white;

    a {
      color: white;

      &:hover {
        color: ${props => props.legacyTheme.colors.sharedGrey};
        text-decoration: none;
      }
    }
  }
`);

const CopyrightBody = cmsLegacyThemeConnector(styled.p`
  font-size: 12px;
  margin: 10px 0 5px;

  a {
    color: #333;
    cursor: pointer;
    text-decoration: underline;
    padding: 10px 0;
  }

  .links {
    &:hover {
      color: #d50f7d;
      text-decoration: none;
    }
  }

  a:focus {
    outline-offset: 2px;
    outline-color: #d40f7d;
    outline-style: solid;
    outline-width: 2px;
  }

  @media screen and (min-width: 768px) {
    a {
      display: inline;
      padding: 0px;
    }
  }

  .tenant-fleetwoodusa & {
    text-align: center;

    a {
      color: white;
      text-decoration: none;

      &:focus {
        outline-color: white;
      }
    }

    .links {
      &:hover {
        color: ${props => props.legacyTheme.colors.sharedGrey};
      }
    }
  }
`);

const FooterHeader = styled.h2`
  margin: 16px 0px 32px 0px;
`;

const svgIcon = {
  linkedin: Linkedin,
  instagram: Instagram,
  youtube: Youtube,
  twitter: Twitter,
  facebook: Facebook,
};

const linkTarget = url => {
  const opensInBlank = Linking.Parse(url)?.opensInBlank;
  return opensInBlank ? '_blank' : null;
};

function FooterColumn({ details, children, index }) {
  const [selectedExpandables, setSelectedExpandables] = useState({});
  const handleToggle = value => () => {
    const copy = { ...selectedExpandables };

    if (copy[value]) {
      delete copy[value];
      return setSelectedExpandables(copy);
    }
    copy[value] = true;
    return setSelectedExpandables(copy);
  };

  const visibleItems = children?.filter(item => isItemVisible(item));

  return (
    <Column key={index}>
      <li className="column--list">
        <TitleContainer
          tabIndex="0"
          className={selectedExpandables[index] ? 'collapsible--expanded' : ''}
          onClick={handleToggle(index)}
        >
          {details && details.title ? <ColumnTitle>{details.title}</ColumnTitle> : null}
          <i className={`icon-chevron-left icon--chevron ${details?.always_expanded ? 'hide_icon' : ''}`} />
        </TitleContainer>
        {visibleItems?.length > 0 && (
          <ul className={`collapsible__content ${details?.always_expanded ? 'always_expanded' : ''}`}>
            {visibleItems.map(({ details: itemDetails }) => {
              const linkFunc = itemDetails?.behavior === 'function' ? pbFunctions()[itemDetails?.functionName] : null;
              return (
                <LinkList
                  className={!itemDetails?.is_icon ? `column--list_item` : `icon__container`}
                  key={itemDetails?.title}
                >
                  {!itemDetails?.is_icon && (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a
                      aria-label={itemDetails?.title}
                      className="child--item_link links"
                      data-track="nav-click"
                      data-track-nav-loc="footer"
                      data-track-sub-level={itemDetails?.title}
                      data-track-top-level={itemDetails?.title}
                      href={linkFunc ? '#' : itemDetails?.url}
                      onClick={linkFunc ? event => linkFunc(event) : null}
                      target={linkTarget(itemDetails?.url)}
                    >
                      <Text content={itemDetails?.title} />
                    </a>
                  )}
                  {itemDetails?.is_icon && (
                    <>
                      {itemDetails?.subheading && <b>{itemDetails?.subheading}</b>}
                      <span className="icon--group">
                        {itemDetails?.iconList?.map(({ selected, url }) =>
                          selected && (
                            <a
                              aria-label={selected}
                              className="icon--wrapper"
                              data-track="nav-click"
                              data-track-nav-loc="footer"
                              data-track-top-level="Social Media"
                              data-track-sub-level={`${selected} ${
                                itemDetails?.subheading ? `-${itemDetails?.subheading}` : ``
                              }`}
                              href={url}
                              key={`icon-${selected}`}
                              rel="noreferrer"
                              target="_blank"
                            >
                              {selected && svgIcon?.[selected] && svgIcon?.[selected]()}
                            </a>
                          )
                        )}
                      </span>
                    </>
                  )}
                </LinkList>
              );
            })}
          </ul>
        )}
      </li>
    </Column>
  );
}

function QuickLinks({ leftAndRight = [] }) {

  return (
    <BottomLinksContents>
      {leftAndRight?.map((leftOrRight, currentIndex) =>
        currentIndex > 1 ? null : (
          <ul>
            {leftOrRight?.map(({ title, behavior, url, functionName }, index) => {
              const clickFunction = behavior === 'function' ? pbFunctions()[functionName] : null;

              return (
                <li>
                  <a
                    aria-label={title}
                    className="links"
                    data-track-nav-loc="footer"
                    data-track-sub-level={title}
                    data-track-top-level={title}
                    data-track="nav-click"
                    href={clickFunction ? '#' : url}
                    target={linkTarget(url)}
                    onClick={clickFunction ? event => clickFunction(event) : null}
                  >
                    <Text content={title} />
                  </a>
                </li>
              );
            })}
          </ul>
        )
      )}
    </BottomLinksContents>
  );
}

function CopyrightContent({ url, paragraph, copyrightText }) {
  const copyright = copyrightText || `© 1996–${new Date().getFullYear()} Owens Corning.`

  return (
    <CopyrightBody>
      <a href={url || '/copyright'} className="links" target={linkTarget(url || '/copyright')}>
         { copyright }
      </a>
      &nbsp;
      <Text content={paragraph} />
    </CopyrightBody>
  );
}

const Footer = connectLocation(
  ({ site, locale: { _code }, headerDetails, footerItems, quickLinks, copyright, ...legacyFooterData }) => {
    const footerClasses = ['oc-footer'];
    const currentYear = new Date().getFullYear();
    const copyrightText = site === 'www.fleetwoodusa.com' ? `©1996-${currentYear} Owens Corning, Fleetwood USA.` : null;

    if ((legacyFooterData?.groups && !headerDetails, !footerItems, !quickLinks, !copyright)) {
      const {
        heading,
        groups,
        registered_statement: registeredStatement,
        bottom_left_links: bottomLeftLinks,
        bottom_right_links: bottomRightLinks,
        bottom_links: bottomLinks,
      } = legacyFooterData;

      const {
        headerDetailsFn,
        footerItemsFn,
        copyrightFn,
        quickLinksFn,
        removeEmptyfooterItems,
      } = restructureLegacyData;

      headerDetails = headerDetailsFn(heading);
      footerItems = footerItemsFn(removeEmptyfooterItems(groups));
      quickLinks = quickLinksFn(bottomLeftLinks, bottomRightLinks, bottomLinks);
      copyright = copyrightFn(registeredStatement);
    }

    return (
      <FooterContainer className={footerClasses.join(' ')}>
        <FooterElements>
          {headerDetails?.has_header && (
            <FooterHeader>
              <Text content={headerDetails?.title} />
            </FooterHeader>
          )}
          <ContentsContainer>
            {footerItems
              ?.filter(item => isItemVisible(item))
              ?.map(({ details, children }, index) => (
                <FooterColumn details={details} children={children} index={index} />
              ))}
          </ContentsContainer>
          <BottomLinksContainer>
            <QuickLinks leftAndRight={[quickLinks?.left, quickLinks?.right]} />
          </BottomLinksContainer>
          <CopyrightContent url={copyright?.url} paragraph={copyright?.paragraph} copyrightText={copyrightText}/>
        </FooterElements>
      </FooterContainer>
    );
  }
);

export default wrappedInReduxProvider(connectLocation(Footer));
/* eslint-enable react/prop-types, react/react-in-jsx-scope */
